import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { CheckCircle2, Loader2, XCircle } from 'lucide-react';

const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('checking'); // checking, success, failed
  const [attempts, setAttempts] = useState(0);
  const { checkAuth } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    
    if (!sessionId) {
      setStatus('failed');
      return;
    }

    const checkPaymentStatus = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/subscription/status/${sessionId}`,
          { credentials: 'include' }
        );

        if (!response.ok) {
          throw new Error('Failed to check status');
        }

        const data = await response.json();

        if (data.payment_status === 'paid') {
          setStatus('success');
          // Refresh auth to get updated subscription
          await checkAuth();
        } else if (data.status === 'expired') {
          setStatus('failed');
        } else if (attempts < 5) {
          // Keep polling
          setTimeout(() => setAttempts(prev => prev + 1), 2000);
        } else {
          setStatus('failed');
        }
      } catch (error) {
        console.error('Error checking payment:', error);
        if (attempts < 5) {
          setTimeout(() => setAttempts(prev => prev + 1), 2000);
        } else {
          setStatus('failed');
        }
      }
    };

    checkPaymentStatus();
  }, [searchParams, attempts, checkAuth]);

  return (
    <div className="min-h-screen bg-gradient-subtle flex items-center justify-center p-4">
      <Card className="max-w-md w-full">
        <CardContent className="pt-8 pb-6 text-center">
          {status === 'checking' && (
            <>
              <Loader2 className="w-16 h-16 animate-spin text-primary mx-auto mb-4" />
              <h2 className="text-xl font-semibold mb-2">Processing Payment...</h2>
              <p className="text-muted-foreground">Please wait while we confirm your payment.</p>
            </>
          )}

          {status === 'success' && (
            <>
              <CheckCircle2 className="w-16 h-16 text-green-500 mx-auto mb-4" />
              <h2 className="text-xl font-semibold mb-2">Payment Successful!</h2>
              <p className="text-muted-foreground mb-6">
                Your subscription is now active. Enjoy Mass Master Pro!
              </p>
              <Button onClick={() => navigate('/')} className="w-full">
                Go to Calculator
              </Button>
            </>
          )}

          {status === 'failed' && (
            <>
              <XCircle className="w-16 h-16 text-destructive mx-auto mb-4" />
              <h2 className="text-xl font-semibold mb-2">Payment Issue</h2>
              <p className="text-muted-foreground mb-6">
                There was an issue with your payment. Please try again or contact support.
              </p>
              <div className="space-y-2">
                <Button onClick={() => navigate('/pricing')} className="w-full">
                  Try Again
                </Button>
                <Button onClick={() => navigate('/')} variant="outline" className="w-full">
                  Go Home
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default PaymentSuccess;
